// src/pages/AutoReplyBuilder/AutoReplyLogs.jsx
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";

const TAKE_OPTIONS = [20, 50, 100];

const formatDateTime = value => {
  if (!value) return "-";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? "-" : dt.toLocaleString();
};

const truncate = (value, max = 100) => {
  const text = String(value || "");
  if (text.length <= max) return text;
  return `${text.slice(0, max)}...`;
};

export default function AutoReplyLogs() {
  const [take, setTake] = useState(20);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosClient.get("autoreplylogs/recent", {
        params: { take },
      });
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load auto-reply logs", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load auto-reply logs.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [take]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="p-6 bg-[#f5f6f7] min-h-[calc(100vh-80px)]">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold text-slate-900">AutoReply Logs</h1>

          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600" htmlFor="ar-logs-take">
              Take
            </label>
            <select
              id="ar-logs-take"
              value={take}
              onChange={e => setTake(Number(e.target.value))}
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {TAKE_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={loadLogs}
              disabled={loading}
              className="inline-flex items-center gap-1.5 h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          {loading && (
            <div className="px-5 py-6 text-sm text-slate-500">Loading logs...</div>
          )}

          {!loading && logs.length === 0 && (
            <div className="px-5 py-6 text-sm text-slate-500">No logs found.</div>
          )}

          {!loading && logs.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Time</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">
                    TriggerType
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">
                    TriggerKeyword
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">
                    FlowName
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">
                    ReplyContent
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">
                    ContactId
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">
                    MessageLogId
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const fullReply = String(log?.replyContent || "");
                  return (
                    <tr key={log?.id} className="border-b border-slate-100">
                      <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                        {formatDateTime(log?.triggeredAt)}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{log?.triggerType || "-"}</td>
                      <td className="px-4 py-2 text-slate-700">
                        {log?.triggerKeyword || "-"}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{log?.flowName || "-"}</td>
                      <td className="px-4 py-2 text-slate-700 max-w-[340px]">
                        <span className="block truncate" title={fullReply || "-"}>
                          {fullReply ? truncate(fullReply, 120) : "-"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-700 font-mono text-xs">
                        {log?.contactId || "-"}
                      </td>
                      <td className="px-4 py-2 text-slate-700 font-mono text-xs">
                        {log?.messageLogId || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

